import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { Subject } from 'rxjs';
import { ToastrService } from 'ngx-toastr';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';

import { DefaultValuesService } from 'src/app/services/defaultvalues.service';
import { Utils } from 'src/app/utils/utils';
import { InvestmentControllingService } from 'src/app/services/investment-controlling.service';


export type MgmtGroupType = 'FLD' | 'MFG' | 'R&D' | null;

export interface MgmtTypeRow {
  value: string;
  groupName: string;
  depth: number;
  selectedType: MgmtGroupType;
  isSelectable: boolean;
  isVisible: boolean;
  hasChildren: boolean;
  expanded: boolean;
  parentValue: string | null;
}

export interface FunctionalAssignmentDto {
  mgmtId: string;
  category: MgmtGroupType;
}

interface MGMTBranchNode {
  Value: string;
  GroupName: string;
  BusinessLevel: string;
  Groups: MGMTBranchNode[];
  GroupFunction?: string;
  IsHistoric?: boolean;
  [key: string]: any;
}

@Component({
  selector: 'app-functional-assignment',
  standalone: true,
  imports: [
    FormsModule,
    MatTableModule,
    MatIconModule,
    MatButtonModule,
    MatButtonToggleModule
  ],
  templateUrl: './functional-assignment.component.html',
  styleUrls: ['./functional-assignment.component.less']
})
export class FunctionalAssignmentComponent implements OnInit, OnDestroy {
  rows: MgmtTypeRow[] = [];
  dataSource = new MatTableDataSource<MgmtTypeRow>([]);
  readonly groupTypes: MgmtGroupType[] = ['FLD', 'MFG', 'R&D'];
  isSaving = false;
  isLoading = false;
  searchTerm = '';

  private readonly _onDestroy = new Subject<void>();

  constructor(
    private readonly http: HttpClient,
    @Inject('BASE_URL') private readonly baseUrl: string,
    public defaults: DefaultValuesService,
    public incoService: InvestmentControllingService,
    private readonly toastr: ToastrService
  ) { }

  ngOnInit(): void {
    this.loadRows();
  }

  ngOnDestroy(): void {
    this._onDestroy.next();
    this._onDestroy.complete();
  }

  loadRows(): void {
    const nodes: MGMTBranchNode[] = this.defaults.managementids ?? [];
    const flatRows: MgmtTypeRow[] = [];
    this.flattenManagementNodes(nodes, flatRows, 0);
    this.rows = flatRows;
    this.fetchSavedConfig();
  }

  private refreshDataSource(): void {
    this.dataSource.data = this.rows.filter(r => r.isVisible);
  }
  
  toggleExpand(row: MgmtTypeRow): void {
    row.expanded = !row.expanded;
    this.setChildrenVisibility(row.value, row.expanded);
    this.refreshDataSource();
  }

  trackByValue(_: number, row: MgmtTypeRow): string {
    return row.value;
  }

  // Generates a guaranteed-unique key for a node,
  // since many nodes have Value: null in the backend.
  private nodeKey(node: MGMTBranchNode): string {
    return node.Value ?? `__name__${node.GroupName}`;
  }

  private flattenManagementNodes(
    nodes: MGMTBranchNode[],
    result: MgmtTypeRow[],
    depth: number,
    parentKey: string | null = null
  ): void {
    for (const node of nodes) {
      if (node.IsHistoric) {
        continue;
      }

      const key = this.nodeKey(node);
      if (node.BusinessLevel === 'MANAGEMENT') {
        result.push({
          value: key,
          groupName: node.GroupName,
          depth,
          selectedType: null,
          isSelectable: true,
          isVisible: true,
          hasChildren: false,
          expanded: false,
          parentValue: parentKey
        });
      } else {
        result.push({
          value: key,
          groupName: node.GroupName,
          depth,
          selectedType: null,
          isSelectable: false,
          isVisible: true,
          hasChildren: !!node.Groups?.length,
          expanded: true,
          parentValue: parentKey
        });
        if (node.Groups?.length) {
          this.flattenManagementNodes(node.Groups, result, depth + 1, key);
        }
      }
    }
  }

  private fetchSavedConfig(): void {
    this.isLoading = true;
    this.incoService.getFunctionalAssignment(
      this,
      (tthis: FunctionalAssignmentComponent, result: any[]) => {
        const map = new Map(result.map((s: any) => [s.mgmtId, s.category as MgmtGroupType]));
        tthis.rows.forEach(row => {
          row.selectedType = map.get(row.value) ?? null;
        });
        tthis.isLoading = false;
        tthis.refreshDataSource();
      },
      (tthis: FunctionalAssignmentComponent, err: any) => {
        Utils.writeLog(tthis.http, tthis.baseUrl, err);
        tthis.isLoading = false;
      }
    );
  }

  onSave(): void {
    const payload: FunctionalAssignmentDto[] = this.rows
      .filter(r => r.isSelectable && !r.value.startsWith('__name__'))
      .map(r => ({
        mgmtId: r.value,
        category: r.selectedType
      }));

    this.isSaving = true;
    this.incoService.upsertFunctionalAssignment(
      payload,
      this,
      (tthis: FunctionalAssignmentComponent) => {
        tthis.isSaving = false;
        tthis.toastr.success('Configuration saved successfully.');
        this.loadRows();
      },
      (tthis: FunctionalAssignmentComponent, err: any) => {
        Utils.writeLog(tthis.http, tthis.baseUrl, err);
        tthis.isSaving = false;
        tthis.toastr.error('An error occurred while saving.');
      }
    );
  }

  applySearch(): void {
    const term = this.searchTerm.trim().toLowerCase();
    if (!term) {
      this.restoreNormalVisibility();
      this.refreshDataSource();
      return;
    }
    const visibleKeys = new Set<string>();
    for (const row of this.rows) {
      const matchesId = row.value.toLowerCase().includes(term);
      const matchesName = row.groupName.toLowerCase().includes(term);
      if (matchesId || matchesName) {
        visibleKeys.add(row.value);
        this.collectAncestorKeys(row, visibleKeys);
      }
    }
    this.rows.forEach(row => { row.isVisible = visibleKeys.has(row.value); });
    this.refreshDataSource();
  }

  private collectAncestorKeys(row: MgmtTypeRow, keys: Set<string>): void {
    if (!row.parentValue || keys.has(row.parentValue)) return;
    keys.add(row.parentValue);
    const parent = this.rows.find(r => r.value === row.parentValue);
    if (parent) this.collectAncestorKeys(parent, keys);
  }

  private restoreNormalVisibility(): void {
    this.rows.forEach(row => {
      row.isVisible = row.parentValue === null || this.isAncestorChainExpanded(row);
    });
  }

  private isAncestorChainExpanded(row: MgmtTypeRow): boolean {
    if (!row.parentValue) return true;
    const parent = this.rows.find(r => r.value === row.parentValue);
    return !!parent && parent.expanded && this.isAncestorChainExpanded(parent);
  }

  private setChildrenVisibility(parentValue: string, visible: boolean): void {
    for (const row of this.rows) {
      if (row.parentValue === parentValue) {
        row.isVisible = visible;
        if (!visible && row.hasChildren && row.expanded) {
          row.expanded = false;
          this.setChildrenVisibility(row.value, false);
        }
      }
    }
  }

}